import { useMutation, useQueryClient } from '@tanstack/react-query'
import { saveRegItem } from 'src/api/queries'
import { RegItem } from 'src/types/regItem'
import { errorMessage } from 'lib/flash-message'
import {
  invalidateRegItemsQuery,
  optimisticallyUpdateRegItem,
} from 'src/routes/(registry)/list/[slug]/utils/registryHelpers'
import NiceModal from '@ebay/nice-modal-react'
import useRegItemChanges, {
  RegItemChanges,
} from 'src/routes/(registry)/list/[slug]/hooks/useRegItemChanges'
import { showChangesSavedToast } from 'src/routes/(registry)/list/[slug]/components/EditRegItemDrawer/components/ChangesSavedToast/showChangesSavedToast'
import ConfirmUnsavedChangesModal from '../components/ConfirmUnsavedChangesModal/ConfirmUnsavedChangesModal'

type UseSaveRegItemProps = {
  regItem: RegItem
  modal: any
}

const useSaveRegItem = ({ regItem, modal }: UseSaveRegItemProps) => {
  const queryClient = useQueryClient()
  const {
    regItemChanges,
    setRegItemChanges,
    hasUnsavedChanges,
    updateRegItemFields,
    clearRegItemFields,
  } = useRegItemChanges({ regItem })

  const saveRegItemMutation = useMutation({
    mutationFn: (regItemDiff: RegItemChanges) =>
      saveRegItem(regItem.registry.id, regItem.id, regItemDiff),
    onSuccess: ({ regItem: newRegItem }: { regItem: RegItem }) => {
      setRegItemChanges({})
      invalidateRegItemsQuery({
        queryClient,
        registryId: newRegItem.registry.id,
      })

      showChangesSavedToast()

      modal.resolve(newRegItem)
      modal.hide()
    },
    onError: (error: any) => {
      errorMessage(
        error?.error?.message ||
          'Failed to save. Try refreshing the page. If the problem persists, contact support.'
      )
    },
  })

  const onSave = () => {
    const newRegItem = { ...regItem, ...regItemChanges }

    optimisticallyUpdateRegItem({
      queryClient,
      updatedRegItem: newRegItem,
    })

    saveRegItemMutation.mutate(newRegItem)
  }

  const handleClose = async () => {
    if (saveRegItemMutation.isLoading) {
      return
    }

    if (hasUnsavedChanges()) {
      const confirmUnsavedChanges: boolean | undefined = await NiceModal.show(
        ConfirmUnsavedChangesModal
      )

      if (!confirmUnsavedChanges) {
        return
      }
    }

    modal.resolve(undefined)
    modal.hide()
  }

  return {
    regItemChanges,
    setRegItemChanges,
    hasUnsavedChanges,
    updateRegItemFields,
    clearRegItemFields,
    saveRegItemMutation,
    handleClose,
    onSave,
  }
}

export default useSaveRegItem
